import React, { useMemo } from 'react';
import { INVENTORY_CATALOG } from '../../lib/inventory';

// Chip cloud + preset buttons for picking which weapons the character is
// proficient with. Stored as a flat array of catalog weapon names + the
// optional sentinels 'simple' and 'martial' (which match all weapons of
// that tier — see lib/equip.js: isProficientWith).
//
// Used by both CharacterSetup step 2 and LevelUpOverlay.
export default function WeaponProficiencyPicker({ value, onChange }) {
  const list = value || [];
  const weapons = useMemo(
    () => INVENTORY_CATALOG.filter((c) => c.category === 'weapon' && c.weapon?.tier),
    [],
  );
  const simpleNames  = weapons.filter((w) => w.weapon.tier === 'simple').map((w) => w.name);
  const martialNames = weapons.filter((w) => w.weapon.tier === 'martial').map((w) => w.name);

  const has = (item) => list.includes(item);

  const toggle = (name) => {
    onChange(has(name) ? list.filter((x) => x !== name) : [...list, name]);
  };

  const addAll = (names) => {
    const next = [...list];
    for (const n of names) if (!next.includes(n)) next.push(n);
    onChange(next);
  };
  const removeAll = (names) => {
    const set = new Set(names);
    onChange(list.filter((x) => !set.has(x)));
  };

  const allSimpleSelected  = simpleNames.every((n) => has(n));
  const allMartialSelected = martialNames.every((n) => has(n));

  return (
    <div style={styles.wrap}>
      <div style={styles.presetRow}>
        <button
          type="button"
          style={{ ...styles.presetBtn, ...(allSimpleSelected ? styles.presetBtnActive : {}) }}
          onClick={() => allSimpleSelected ? removeAll(simpleNames) : addAll(simpleNames)}
        >
          {allSimpleSelected ? '−' : '+'} Simple weapons
        </button>
        <button
          type="button"
          style={{ ...styles.presetBtn, ...(allMartialSelected ? styles.presetBtnActive : {}) }}
          onClick={() => allMartialSelected ? removeAll(martialNames) : addAll(martialNames)}
        >
          {allMartialSelected ? '−' : '+'} Martial weapons
        </button>
      </div>

      <div style={styles.tierBlock}>
        <div style={styles.tierLabel}>Simple</div>
        <div style={styles.chips}>
          {simpleNames.map((name) => (
            <button
              key={name}
              type="button"
              style={{ ...styles.chip, ...(has(name) ? styles.chipActive : {}) }}
              onClick={() => toggle(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.tierBlock}>
        <div style={styles.tierLabel}>Martial</div>
        <div style={styles.chips}>
          {martialNames.map((name) => (
            <button
              key={name}
              type="button"
              style={{ ...styles.chip, ...(has(name) ? styles.chipActive : {}) }}
              onClick={() => toggle(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.hint}>
        Proficient weapons get the proficiency bonus added to your quick-attack to-hit rolls.
      </div>
    </div>
  );
}

const styles = {
  wrap:          { display: 'flex', flexDirection: 'column', gap: 8 },
  presetRow:     { display: 'flex', gap: 6 },
  presetBtn:     { flex: 1, background: '#0e1528', border: '1px solid #4a3d28', color: '#9a8b6e', padding: '6px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
  presetBtnActive:{ background: 'rgba(212,168,71,0.18)', borderColor: '#8b7340', color: '#d4a847' },
  tierBlock:     { display: 'flex', flexDirection: 'column', gap: 4 },
  tierLabel:     { fontSize: 9, color: '#9a8b6e', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' },
  chips:         { display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip:          { background: '#0e1528', border: '1px solid #4a3d28', color: '#9a8b6e', padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 14, letterSpacing: '0.02em' },
  chipActive:    { background: 'rgba(212,168,71,0.2)', borderColor: '#d4a847', color: '#d4a847', boxShadow: '0 0 6px rgba(212,168,71,0.3)' },
  hint:          { fontSize: 11, color: '#6a5d45', fontStyle: 'italic' },
};
